import { Link } from '@remix-run/react';
import {
    Box,
    Flex,
    TileGrid,
    Button,
    useStyles,
    useAppDisplayModeContext,
    pxToRem,
    HotButtons,
} from '@archipro-design/aria';
import * as S from '../product-range/ProductRange.style';
import ProductTile from '@modules/tile/component/product-tile/ProductTile';
import SaveToDesignBoard from '@modules/design-board/component/SaveToDesignBoard';
import { useSaveToDesignBoard } from '~/modules/design-board/hook/use-save-to-design-board';
import { useAppData } from '~/modules/root/hook/use-app-data';
import { useDeleteItemWithConfirm } from '~/modules/directory/hook/use-delete-item-with-confirm';
import { getHotButtons } from '~/modules/directory/util/get-hot-buttons';
import { getAdminCaption } from '~/modules/directory/util/get-admin-caption';
import { visitBMEditor } from '~/modules/directory/util/tile-helpers';
import DeleteItemConfirmModal from '~/modules/directory/component/DeleteItemConfirmModal';
import AdminCaption from '~/modules/tile/component/admin-caption/AdminCaption';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { useToggleHomePin } from '~/modules/home/hook/use-toggle-home-pin';
import { useState } from 'react';
import MessageModal from '~/modules/home/component/common/MessageModal';
import { canHomePin } from '~/modules/home/util/home-pin-helpers';

interface AlsoFromProductsProps {
    data: ProductPageLoaderData['product'] & { HasRanges: boolean };
}

const TILES_DISPLAYED_DESKTOP = 10;
const TILES_DISPLAYED_MOBILE = 4;

const AlsoFromProducts = ({
    data: { AlsoFromProducts, HasRanges, Professional },
}: AlsoFromProductsProps) => {
    const { css } = useStyles();
    const appDisplayMode = useAppDisplayModeContext();
    const { mobile } = appDisplayMode;
    const { favouriteItem } = useSaveToDesignBoard('ProductPin');

    const { rootGrants } = useAppData();
    const canEditAdmin = rootGrants.editProfile;
    const {
        itemID: itemIDToDelete,
        willDelete,
        confirmDelete,
        cancelDelete,
    } = useDeleteItemWithConfirm();

    const [pinErrorMessage, setPinErrorMessage] = useState<string | null>(null);
    const { toggleHomePin } = useToggleHomePin(canEditAdmin, (error) => {
        if (error.Message) {
            setPinErrorMessage(error.Message);
        }
    });

    if (HasRanges || !AlsoFromProducts || !AlsoFromProducts.edges?.length) {
        return null;
    }

    const { pageInfo, edges } = AlsoFromProducts;

    const totalCount = pageInfo?.totalCount;
    const tilesDisplayed = mobile
        ? TILES_DISPLAYED_MOBILE
        : TILES_DISPLAYED_DESKTOP;
    const viewMoreProducts = totalCount > tilesDisplayed;

    return (
        <Box className={css(S.ProductRangeWrapper)}>
            <TileGrid
                heading={{
                    children: (
                        <>
                            More from
                            <br />
                            {Professional.Title}
                        </>
                    ),
                    variables: {
                        fontWeight: 500,
                    },
                }}
                className={css(S.ProductRangeGrid)}
                columns={mobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)'}
            >
                {edges.slice(0, tilesDisplayed).map((tile, idx) => {
                    const hotButtons = getHotButtons({
                        item: tile.node,
                        itemType: 'product',
                        appDisplayMode,
                        canEditAdmin,
                        canEdit: Boolean(tile.node.canEdit),
                        onEditClick: visitBMEditor,
                        onDeleteClick: () => willDelete(tile.node.ID as number),
                        onRepositionClick: (item) => {
                            visitBMEditor(item, false, {
                                repositionThumb: true,
                            });
                        },
                        pinned: !!tile.node.admin?.homePinned,
                        onPinClick: canHomePin(rootGrants)
                            ? () => {
                                  if (!tile.node.admin) {
                                      return;
                                  }
                                  toggleHomePin(
                                      Number(tile.node.ID),
                                      !tile.node.admin.homePinned
                                  );
                              }
                            : undefined,
                    });
                    const adminCaption = getAdminCaption({
                        appDisplayMode,
                        item: tile.node.admin,
                        canEditAdmin,
                    });

                    return (
                        <ProductTile
                            key={`also-from-product-tile-${idx}`}
                            data={{
                                Product: tile.node,
                                Professional,
                            }}
                            variant={!mobile ? '01' : '02'}
                            size={!mobile ? 'large' : 'medium'}
                            favouriteItem={favouriteItem}
                            variables={{
                                hoverColor: 'inherit',
                            }}
                            topActions={
                                hotButtons ? (
                                    <HotButtons items={hotButtons} />
                                ) : undefined
                            }
                            adminCaption={
                                adminCaption ? (
                                    <AdminCaption adminCaption={adminCaption} />
                                ) : undefined
                            }
                        />
                    );
                })}
            </TileGrid>
            {viewMoreProducts && (
                <Flex
                    className={css({
                        margin: mobile ? '0 auto' : `0 0 0 ${pxToRem(924)}`,
                    })}
                >
                    <Button
                        as={Link}
                        prefetch="intent"
                        to={`${Professional.Link}/our-products`}
                        size={16}
                        className={css(S.ViewRangeButton)}
                        outlined={true}
                    >
                        View more
                    </Button>
                </Flex>
            )}
            {favouriteItem.target && (
                <SaveToDesignBoard {...favouriteItem} autoReposition={false} />
            )}
            {itemIDToDelete && (
                <DeleteItemConfirmModal
                    title={'Delete product'}
                    content={
                        'Are you sure you would like to delete this product? All information and associated tags will be lost.'
                    }
                    open={true}
                    onCancel={cancelDelete}
                    onConfirm={confirmDelete}
                />
            )}
            {pinErrorMessage && (
                <MessageModal
                    title={'Warning'}
                    content={pinErrorMessage}
                    open={true}
                    onConfirm={() => setPinErrorMessage(null)}
                />
            )}
        </Box>
    );
};

export default AlsoFromProducts;
